import React, { useState, useRef, useEffect } from 'react';
import { Send, Copy, Check } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import './ChatDetail.css';
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Card } from "./ui/card"
import PalmerLogo from './PalmerLogo';

// ChatDetail component to display chat messages
const ChatDetail = ({ chat }) => {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [chatData, setChatData] = useState(chat);
  const [copiedId, setCopiedId] = useState(null);
  const [ratedResponses, setRatedResponses] = useState({});
  const [ratingSubmitting, setRatingSubmitting] = useState({});
  const messagesEndRef = useRef(null);

  const handleCopy = (text, messageId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(messageId);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(err => console.error('Failed to copy:', err));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRate = async (responseId, value) => {
    // avoid duplicate submissions
    if (ratingSubmitting[responseId]) return;
    setRatingSubmitting(prev => ({ ...prev, [responseId]: true }));

    // resolve user id from localStorage or token
    let userObj = null;
    try { const raw = localStorage.getItem('user'); if (raw) userObj = JSON.parse(raw); } catch (e) { }
    let userId = (userObj && (userObj.id || userObj.user_id || userObj._id));
    if (!userId) {
      const tk = localStorage.getItem('token');
      if (tk) {
        try { const payload = JSON.parse(atob(tk.split('.')[1])); userId = payload.user_id || payload.userId || payload.sub || payload.id; } catch (e) { }
      }
    }

    const body = {
      response_rating: value,
      rating_response_id: responseId,
      rating_user_id: userId
    };

    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}${API_ENDPOINTS.RESPONSE_RATING}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        console.error('Failed to submit rating', resp.status, txt);
      } else {
        // mark rated locally
        setRatedResponses(prev => ({ ...prev, [responseId]: value }));
      }
    } catch (err) {
      console.error('Error submitting rating', err);
    } finally {
      setRatingSubmitting(prev => ({ ...prev, [responseId]: false }));
    }
  };

  useEffect(() => {
    // update local chatData when parent passes a different chat
    setChatData(chat);
  }, [chat]);

  useEffect(() => {
    scrollToBottom();
  }, [chatData]);

  if (!chat) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <p className="text-muted-foreground">Select a chat to view the conversation</p>
      </div>
    );
  }

  // Combine prompts and responses from local chatData, sort by creation time
  const messages = [
    ...(chatData?.prompts || []),
    ...(chatData?.responses || []),
  ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const fetchChat = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHAT}${chat.id}/`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        console.error('Failed to fetch chat after update', resp.status);
        return;
      }
      const data = await resp.json();
      setChatData(data);
    } catch (err) {
      console.error('Error fetching chat:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const promptText = (inputValue || '').trim();
    if (!promptText) return;

    // optimistic UI: add the prompt and a placeholder response
    const promptId = `local-prompt-${Date.now()}`;
    const responseId = `local-response-${Date.now()}`;
    const now = new Date().toISOString();
    const newPrompt = {
      id: promptId,
      prompt_content: promptText,
      created_at: now,
      chat_id: chat.id,
    };
    const analysingResponse = {
      id: responseId,
      response_content: 'Palmer is analysing...',
      created_at: now,
      chat_id: chat.id,
      status: 'sending',
    };

    setChatData(prev => ({
      ...(prev || {}),
      prompts: [...(prev?.prompts || []), newPrompt],
      responses: [...(prev?.responses || []), analysingResponse],
    }));

    setIsSending(true);
    setInputValue('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHAT}${chat.id}/add/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ "prompt": promptText }),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('Failed to post prompt:', res.status, text);
        // replace the analysing placeholder with an error message
        setChatData(prev => {
          if (!prev) return prev;
          const updatedResponses = (prev.responses || []).map(r =>
            r.id === responseId ? { ...r, response_content: 'Failed to send prompt', status: 'error' } : r
          );
          return { ...prev, responses: updatedResponses };
        });
      } else {
        // Refresh chatData from server to pick up new prompt/response
        await fetchChat();
      }
    } catch (err) {
      console.error('Error sending prompt:', err);
    } finally {
      setIsSending(false);
      // ensure we scroll to bottom after any update
      setTimeout(() => scrollToBottom(), 150);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background overflow-y-auto">
      {/* Header */}
      <div className="py-10 lg:py-14">
        <div className="max-w-4xl px-4 sm:px-6 lg:px-8 mx-auto text-center">
          <div className="mb-4 flex justify-center items-center">
            <PalmerLogo className="w-28 h-6" />
          </div>
          <h1 className="text-3xl font-black text-foreground sm:text-4xl">
            Welcome to Palmer AI
          </h1>
          <p className="mt-3 text-muted-foreground">
            Your AI-powered sports betting assistant
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1">
        <div className="max-w-4xl mx-auto px-4 pb-28">
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${'prompt_content' in message ? 'justify-end' : 'justify-start'
                  }`}
              >
                <Card
                  className={`relative p-4 rounded-lg group max-w-[80%] ${'prompt_content' in message
                    ? 'bg-primary text-primary-foreground' // User message
                    : 'bg-card text-card-foreground' // AI response
                    }`}
                >
                  {/* Copy Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCopy(
                      'prompt_content' in message ? message.prompt_content : message.response_content,
                      message.id
                    )}
                    className="absolute -top-3 -right-3 h-6 w-6 rounded-full bg-muted text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Copy text"
                  >
                    {copiedId === message.id ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </Button>

                  {'prompt_content' in message ? (
                    <div className="whitespace-pre-wrap break-words">{message.prompt_content}</div>
                  ) : (
                    message.status === 'sending' ? (
                      <div className="italic opacity-70">
                        <span className="inline-block animate-pulse">Palmer is analysing...</span>
                      </div>
                    ) : (
                      <>
                        <div
                          className="prose dark:prose-invert prose-p:m-0 prose-headings:mb-4 prose-ul:m-0 prose-ul:list-none w-full break-words whitespace-pre-wrap chat-message"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(message.response_content || '')) }}
                        />

                        {/* Rating buttons (1-5) below each response */}
                        <div className="mt-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto">
                          <span className="text-xs text-muted-foreground">Rate:</span>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((val) => (
                              <Button
                                key={val}
                                variant={ratedResponses[message.id] === val ? "default" : "outline"}
                                size="icon"
                                onClick={() => handleRate(message.id, val)}
                                disabled={!!ratingSubmitting[message.id]}
                                className={`w-7 h-7 text-xs rounded-full ${ratedResponses[message.id] === val ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                title={`Rate ${val}`}
                              >
                                {val}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </>
                    )
                  )}
                </Card>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Floating Input (sticky at viewport bottom) */}
      <div className="sticky bottom-0 pointer-events-none">
        <div className="max-w-4xl mx-auto px-4 pb-6">
          <Card className="pointer-events-auto shadow-lg px-4 py-3 transform translate-y-2 flex items-center gap-2">
            <form onSubmit={handleSubmit} className="flex-1 flex items-center gap-2">
              <Input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={isSending}
                placeholder="Ask me anything..."
                className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
              />
              <Button
                type="submit"
                disabled={isSending}
                size="icon"
                className={isSending ? 'opacity-50' : ''}
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChatDetail;